// tour.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap, map, withLatestFrom, filter, switchMap, take } from 'rxjs/operators';
import * as TourActions from './tour.actions';
import { TourService } from '../../services/tour.service';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectTourState, selectTourActive } from './tour.selectors';
import { of } from 'rxjs';

@Injectable()
export class TourEffects {
  // Start tour effect
  startTour$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TourActions.startTour),
        withLatestFrom(this.store.select(selectTourState)),
        tap(([action, state]) => {
          // Use the current route if none provided
          const route = action.route || this.getCurrentRoute();

          // CRITICAL: Check if this tour is already completed in localStorage
          if (route === '/icp-optimization' &&
            localStorage.getItem('salesommo_tour_completed_icp') === 'true') {
            console.log('ICP tour already completed in localStorage, not showing (from effect)');
            return; // Don't start the tour
          }

          if ((route === '/' || route === '/dashboard') &&
            localStorage.getItem('salesommo_tour_completed_dashboard') === 'true') {
            console.log('Dashboard tour already completed in localStorage, not showing (from effect)');
            return; // Don't start the tour
          }

          console.log(`Starting tour for route: ${route}`);

          // Store some basic state in localStorage
          localStorage.setItem('salesommo_tour_state', JSON.stringify({
            route,
            stepIndex: 0,
            timestamp: Date.now()
          }));

          // Start the tour
          this.tourService.startTour();
        })
      ),
    { dispatch: false }
  );

  // Complete tour effect - use more NgRx, less localStorage
  completeTour$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TourActions.completeTour),
        tap(() => {
          // We'll still need to set this for persistence across page refreshes
          localStorage.setItem('appTourCompleted', 'true');

          // Page-specific tour completion is now handled in the reducer
        })
      ),
    { dispatch: false }
  );

  // Cancel tour effect
  cancelTour$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TourActions.cancelTour),
        tap(() => {
          // Get current route
          const route = this.getCurrentRoute();

          // IMPORTANT: Directly set tour completion for the current page
          if (route === '/icp-optimization') {
            localStorage.setItem('salesommo_tour_completed_icp', 'true');
          } else if (route === '/' || route === '/dashboard') {
            localStorage.setItem('salesommo_tour_completed_dashboard', 'true');
          }

          // Verify the write
          console.log('Tour cancelled - localStorage state:', {
            dashboardTourCompleted: localStorage.getItem('salesommo_tour_completed_dashboard'),
            icpTourCompleted: localStorage.getItem('salesommo_tour_completed_icp')
          });

          // Clear active tour state - use the string directly instead of this.TOUR_STATE_KEY
          localStorage.removeItem('salesommo_tour_state');
        })
      ),
    { dispatch: false }
  );

  // Mark a specific page's tour as completed
  markPageTourAsCompleted(route: string): void {
    if (route === '/icp-optimization') {
      localStorage.setItem('appTourCompleted_icp', 'true');
    } else if (route === '/' || route === '/dashboard') {
      localStorage.setItem('appTourCompleted_dashboard', 'true');
    }
  }

  // Listen to the tour service's active state
  tourActiveChange$ = createEffect(() =>
    this.tourService.tourActive$.pipe(
      map(active => TourActions.setTourActive({ active }))
    )
  );

  // Advance step effect
  advanceStep$ = createEffect(() =>
      this.actions$.pipe(
        ofType(TourActions.advanceStep),
        withLatestFrom(this.store.select(selectTourState))
      ),
    { dispatch: false }
  );

  // Route change effect - handle tour continuation
  routeChange$ = createEffect(() =>
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        withLatestFrom(this.store.select(selectTourState)),
        switchMap(([event, state]) => {
          const newRoute = this.getMainRoute((event as NavigationEnd).urlAfterRedirects);
          // console.log(`Route changed to: ${newRoute} (from ${state.currentRoute})`);

          // First dispatch route change action
          this.store.dispatch(TourActions.setCurrentRoute({ route: newRoute }));

          // Check if we were in the middle of a tour on the previous page
          if (state.active) {
            console.log('Tour was active during navigation');

            // Check if the new page should continue the tour
            if (this.shouldContinueTour(state.currentRoute, newRoute)) {
              console.log('Will continue tour on new page');

              // Small delay to ensure component is initialized
              setTimeout(() => {
                this.store.dispatch(TourActions.resumeTour({
                  route: newRoute,
                  stepIndex: 0  // Always start at beginning of new page's tour
                }));
              }, 500);
            }
          }

          return of(null); // Just to satisfy the return type, won't dispatch anything
        })
      ),
    { dispatch: false }
  );

  /**
   * Determine if tour should continue between pages
   */
  private shouldContinueTour(fromRoute: string, toRoute: string): boolean {
    // Define valid tour paths
    const validPaths = [
      { from: '/', to: '/icp-optimization' },
      { from: '/dashboard', to: '/icp-optimization' }
      // Add other valid paths as needed
    ];

    return validPaths.some(path => path.from === fromRoute && path.to === toRoute);
  }

  constructor(
    private actions$: Actions,
    private tourService: TourService,
    private router: Router,
    private store: Store
  ) {}

  // Helper methods
  private getCurrentRoute(): string {
    return this.getMainRoute(this.router.url);
  }

  private getMainRoute(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
